import type { GameTimingSnapshot, StoneColor } from '@wx-weiqi/contracts';

export interface ProjectedByoyomi {
  readonly periods: number;
  readonly periodRemainingMs: number;
}

export interface ClockProjectionAnchor {
  readonly serverEpochMs: number;
  readonly receivedAtMonotonicMs: number;
}

export function createClockProjectionAnchor(
  serverAsOf: string,
  receivedAtMonotonicMs: number,
): ClockProjectionAnchor {
  return Object.freeze({
    serverEpochMs: Date.parse(serverAsOf),
    receivedAtMonotonicMs,
  });
}

export function projectedServerNow(
  anchor: ClockProjectionAnchor,
  currentMonotonicMs: number,
): number {
  return anchor.serverEpochMs + Math.max(0, currentMonotonicMs - anchor.receivedAtMonotonicMs);
}

export function projectedRemainingMs(
  timing: GameTimingSnapshot,
  color: StoneColor,
  nowEpochMs: number,
): number | null {
  const player = color === 'BLACK' ? timing.black : timing.white;
  if (player.remainingMs === null) return null;
  if (timing.status !== 'RUNNING' || timing.activeColor !== color) return player.remainingMs;
  return Math.max(0, player.remainingMs - Math.max(0, nowEpochMs - Date.parse(timing.asOf)));
}

export function projectedByoyomi(
  timing: GameTimingSnapshot,
  color: StoneColor,
  nowEpochMs: number,
): ProjectedByoyomi | null {
  if (timing.byoyomi === undefined || timing.byoyomi === null) return null;
  const player = color === 'BLACK' ? timing.black : timing.white;
  if (
    player.remainingMs === null ||
    player.byoyomiPeriodsRemaining === undefined ||
    player.byoyomiPeriodsRemaining === null ||
    player.byoyomiPeriodRemainingMs === undefined ||
    player.byoyomiPeriodRemainingMs === null
  ) {
    return null;
  }
  let periods = player.byoyomiPeriodsRemaining;
  let remaining = player.byoyomiPeriodRemainingMs;
  if (timing.status === 'RUNNING' && timing.activeColor === color) {
    let liveElapsed = Math.max(0, nowEpochMs - Date.parse(timing.asOf));
    if (player.remainingMs > 0) {
      if (liveElapsed < player.remainingMs) return null;
      liveElapsed -= player.remainingMs;
    }
    if (liveElapsed >= remaining) {
      liveElapsed -= remaining;
      periods = Math.max(0, periods - 1);
      if (periods === 0) remaining = 0;
      else {
        const consumedFullPeriods = Math.min(
          periods,
          Math.floor(liveElapsed / timing.byoyomi.periodTimeMs),
        );
        periods -= consumedFullPeriods;
        remaining =
          periods === 0
            ? 0
            : timing.byoyomi.periodTimeMs - (liveElapsed % timing.byoyomi.periodTimeMs);
      }
    } else {
      remaining -= liveElapsed;
    }
  } else if (player.remainingMs > 0) {
    return null;
  }
  return { periods, periodRemainingMs: Math.max(0, remaining) };
}

export function formatDuration(milliseconds: number | null): string {
  if (milliseconds === null) return '不限时';
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1_000));
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0
    ? `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    : `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
